import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Tab, 
  Tabs, 
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemAvatar,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { processDocument, queryDocument } from '../utils/aiService';

// TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`document-tabpanel-${index}`}
      aria-labelledby={`document-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// DocumentItem component
const DocumentItem = ({ document, onDelete, onDownload, onProcess, onQuery, isAdmin }) => {
  const [queryDialogOpen, setQueryDialogOpen] = useState(false);
  const [queryText, setQueryText] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const getDocumentIcon = () => {
    if (document.document_type === 'SOW') {
      return <ArticleIcon color="primary" />;
    } else if (document.has_pdf) {
      return <PictureAsPdfIcon color="error" />;
    } else if (document.has_docx) {
      return <DescriptionIcon color="primary" />;
    } else {
      return <InsertDriveFileIcon />;
    }
  };

  const handleOpenQueryDialog = () => {
    setQueryDialogOpen(true);
    setQueryResult(null);
  };

  const handleCloseQueryDialog = () => {
    setQueryDialogOpen(false);
  };

  const handleQuerySubmit = async () => {
    if (!queryText) return;
    
    setLoading(true);
    try {
      const result = await onQuery(document.document_id, queryText);
      setQueryResult(result);
    } catch (error) {
      console.error('Error querying document:', error);
      setQueryResult({
        success: false,
        error: error.message || 'Failed to query document'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProcessDocument = async () => {
    try {
      await onProcess(document.document_id);
      alert(`Document "${document.title}" has been processed for AI querying.`);
    } catch (error) {
      console.error('Error processing document:', error);
      alert('Failed to process document: ' + (error.message || 'Unknown error'));
    }
  };

  // Check if document has been AI processed
  const isProcessed = document.metadata && document.metadata.ai_processed;

  return (
    <>
      <ListItem>
        <ListItemAvatar>
          <Avatar>
            {getDocumentIcon()}
          </Avatar>
        </ListItemAvatar>
        <ListItemText 
          primary={document.title} 
          secondary={
            <>
              <Typography variant="body2" color="text.secondary">
                {document.document_type === 'SOW' ? 'Project Documents' : document.document_type || 'Document'}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {document.tags && document.tags.map((tag, index) => (
                  <Chip 
                    key={index} 
                    label={tag.name} 
                    size="small" 
                    sx={{ mr: 0.5, mb: 0.5, backgroundColor: tag.color || '#F5821F' }}
                  />
                ))}
              </Box>
            </>
          } 
        />
        <ListItemSecondaryAction>
          {onProcess && (
            <IconButton edge="end" aria-label="process" onClick={handleProcessDocument} disabled={isProcessed}>
              <SmartToyIcon color={isProcessed ? "disabled" : "primary"} />
            </IconButton>
          )}
          {isProcessed && onQuery && (
            <IconButton edge="end" aria-label="query" onClick={handleOpenQueryDialog}>
              <SmartToyIcon color="success" />
            </IconButton>
          )}
          <IconButton edge="end" aria-label="download" onClick={() => onDownload(document)}>
            <DownloadIcon />
          </IconButton>
          {isAdmin && (
            <IconButton edge="end" aria-label="delete" onClick={() => onDelete(document)}>
              <DeleteIcon />
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItem>
      <Dialog open={queryDialogOpen} onClose={handleCloseQueryDialog} maxWidth="md" fullWidth>
        <DialogTitle>Ask AI about this document</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="query"
            label="Ask a question about this document"
            type="text"
            fullWidth
            variant="outlined"
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          )}
          
          {queryResult && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Answer:
              </Typography>
              {queryResult.success ? (
                <>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {queryResult.answer}
                  </Typography>
                  
                  {queryResult.sources && queryResult.sources.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Sources:
                      </Typography>
                      <List dense>
                        {queryResult.sources.map((source, index) => (
                          <ListItem key={index}>
                            <ListItemText 
                              primary={source.content} 
                              secondary={`From: ${source.metadata.document_name || document.title}`}
                              primaryTypographyProps={{ 
                                variant: 'body2',
                                sx: { fontStyle: 'italic' }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </>
              ) : (
                <Typography color="error">
                  {queryResult.error || 'Failed to get answer. Please try a different question.'}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQueryDialog}>Close</Button>
          <Button onClick={handleQuerySubmit} variant="contained" disabled={!queryText || loading}>
            Ask
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// DocumentUploadDialog component
const DocumentUploadDialog = ({ open, onClose, onUpload, projectId, docType }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setLoading(true);
    try {
      await onUpload(file, projectId, docType);
      onClose();
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Upload {docType === 'SOW' ? 'Project Documents' : 'Document'}</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <input
            accept=".pdf,.docx,.doc,.md,.txt"
            id="document-file"
            type="file"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <label htmlFor="document-file">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadFileIcon />}
              fullWidth
            >
              Select File
            </Button>
          </label>
          
          {file && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1">
                Selected file: {file.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Size: {Math.round(file.size / 1024)} KB
              </Typography>
            </Box>
          )}
          
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleUpload} 
          variant="contained" 
          disabled={!file || loading}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main ProjectDocumentsPanel component
const ProjectDocumentsPanel = ({
  project,
  onUploadDocument,
  onDeleteDocument,
  onDownloadDocument,
  onRefresh,
  isAdmin
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('');
  
  // Organize documents by type
  const [documents, setDocuments] = useState({
    sow: [],
    specs: [],
    requirements: [],
    other: []
  });
  
  useEffect(() => {
    if (project && project.documents) {
      setDocuments({
        sow: project.documents.sow || [],
        specs: [...(project.documents.functional_specs || []), ...(project.documents.technical_specs || [])],
        requirements: project.documents.requirements || [],
        other: project.documents.other || []
      });
    }
  }, [project]);
  
  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleOpenUploadDialog = (docType) => {
    setUploadDocType(docType);
    setUploadDialogOpen(true);
  };
  
  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
  };
  
  const handleProcessDocument = async (documentId) => {
    try {
      await processDocument(documentId);
      if (onRefresh) {
        onRefresh();
      }
      return true;
    } catch (error) {
      console.error('Error processing document:', error);
      return false;
    }
  };
  
  const handleQueryDocument = async (documentId, query) => {
    try {
      const result = await queryDocument(documentId, query);
      return result;
    } catch (error) {
      console.error('Error querying document:', error);
      throw error;
    }
  };
  
  // Get document count for tab labels
  const sowCount = documents.sow?.length || 0;
  const specsCount = documents.specs?.length || 0;
  const reqCount = documents.requirements?.length || 0;
  const otherCount = documents.other?.length || 0;
  
  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleChangeTab} 
          aria-label="document tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={`Project Documents (${sowCount})`} />
          <Tab label={`Specifications (${specsCount})`} />
          <Tab label={`Requirements (${reqCount})`} />
          <Tab label={`Other Documents (${otherCount})`} />
        </Tabs>
      </Box>
      
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<UploadFileIcon />}
            onClick={() => handleOpenUploadDialog('SOW')}
          >
            Upload Project Documents
          </Button>
        </Box>
        
        <List>
          {documents.sow && documents.sow.length > 0 ? (
            documents.sow.map((doc) => (
              <DocumentItem
                key={doc.id}
                document={doc}
                onDelete={onDeleteDocument}
                onDownload={onDownloadDocument}
                onProcess={handleProcessDocument}
                onQuery={handleQueryDocument}
                isAdmin={isAdmin}
              />
            ))
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No project documents have been uploaded.
            </Typography>
          )}
        </List>
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<UploadFileIcon />}
            onClick={() => handleOpenUploadDialog('SPEC')}
          >
            Upload Specification
          </Button>
        </Box>
        
        <List>
          {documents.specs && documents.specs.length > 0 ? (
            documents.specs.map((doc) => (
              <DocumentItem
                key={doc.id}
                document={doc}
                onDelete={onDeleteDocument}
                onDownload={onDownloadDocument}
                onProcess={handleProcessDocument}
                onQuery={handleQueryDocument}
                isAdmin={isAdmin}
              />
            ))
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No specification documents have been uploaded.
            </Typography>
          )}
        </List>
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<UploadFileIcon />}
            onClick={() => handleOpenUploadDialog('REQ')}
          >
            Upload Requirements
          </Button>
        </Box>
        
        <List>
          {documents.requirements && documents.requirements.length > 0 ? (
            documents.requirements.map((doc) => (
              <DocumentItem
                key={doc.id}
                document={doc}
                onDelete={onDeleteDocument}
                onDownload={onDownloadDocument}
                onProcess={handleProcessDocument}
                onQuery={handleQueryDocument}
                isAdmin={isAdmin}
              />
            ))
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No requirement documents have been uploaded.
            </Typography>
          )}
        </List>
      </TabPanel>
      
      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<UploadFileIcon />}
            onClick={() => handleOpenUploadDialog('OTHER')}
          >
            Upload Document
          </Button>
        </Box>
        
        <List>
          {documents.other && documents.other.length > 0 ? (
            documents.other.map((doc) => (
              <DocumentItem
                key={doc.id}
                document={doc}
                onDelete={onDeleteDocument}
                onDownload={onDownloadDocument}
                onProcess={handleProcessDocument}
                onQuery={handleQueryDocument}
                isAdmin={isAdmin}
              />
            ))
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No other documents have been uploaded.
            </Typography>
          )}
        </List>
      </TabPanel>
      
      <DocumentUploadDialog 
        open={uploadDialogOpen} 
        onClose={handleCloseUploadDialog} 
        onUpload={onUploadDocument}
        projectId={project?.id}
        docType={uploadDocType}
      />
    </Paper>
  );
};

export default ProjectDocumentsPanel;